import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { api } from '@/api';
import { PaginationSearchParams, SearchQuery, SortOrderQuery } from '@/api/types';
import { PaginatedLogisticProviderListFilters } from '@/api/apis/facilities';
import { partnerProvidersQueryKeys } from '@/queries/partner-providers/partner-providers.keys';
import { DEFAULT_INFINITE_QUERY_OPTIONS } from '@/queries/utils';

const allLogisticProvidersPaginationParams: PaginationSearchParams = {
  itemsPerPage: 100,
  pageIndex: 0,
};

export const getProvidersByIds = async (providersList: string[]) => {
  const providersIdsSet = new Set(providersList);
  const providersIds = Array.from(providersIdsSet);

  if (providersIds) {
    return api.partnerprovider.getPartnerProvidersList({
      ids: providersIds.join(','),
      pageIndex: 0,
      itemsPerPage: providersIds.length,
    });
  }

  return null;
};

export const partnerProvidersQueries = {
  all: () =>
    queryOptions({
      queryKey: partnerProvidersQueryKeys.getList(allLogisticProvidersPaginationParams),
      queryFn: () => api.partnerprovider.getPartnerProvidersList(allLogisticProvidersPaginationParams),
    }),
  list: (listQuery: PaginatedLogisticProviderListFilters) =>
    queryOptions({
      queryKey: partnerProvidersQueryKeys.getList(listQuery),
      queryFn: () => api.partnerprovider.getPartnerProvidersList(listQuery),
      placeholderData: keepPreviousData,
    }),
  infiniteList: (searchPagination: PaginatedLogisticProviderListFilters) =>
    infiniteQueryOptions({
      queryKey: partnerProvidersQueryKeys.getInfiniteList(searchPagination),
      queryFn: ({ pageParam: pageIndex }) =>
        api.partnerprovider.getPartnerProvidersList({ ...searchPagination, pageIndex }),
      placeholderData: keepPreviousData,
      ...DEFAULT_INFINITE_QUERY_OPTIONS,
    }),
  item: (txCenterId: string) =>
    queryOptions({
      queryKey: partnerProvidersQueryKeys.getItem(txCenterId),
      queryFn: () => api.partnerprovider.getPartnerProvidersById(txCenterId),
    }),
};

const useInfinitePartnerProvidersListQuery = (filters: SearchQuery & SortOrderQuery) => {
  return useInfiniteQuery(partnerProvidersQueries.infiniteList({ itemsPerPage: 10, pageIndex: 0, ...filters }));
};

const usePartnerProvidersListQuery = (listQuery: PaginatedLogisticProviderListFilters) => {
  return useQuery(partnerProvidersQueries.list(listQuery));
};

export { useInfinitePartnerProvidersListQuery, usePartnerProvidersListQuery };
